/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from 'react';
import {
  Action,
  ActionIcon,
  FontWeight,
  Grid,
  ICON_CLOSE,
  ICON_IMAGE_OUTLINE,
  ICON_PLUS,
  ICON_SYNC,
  Message,
  Portal,
  Stack,
  Text,
  useModal,
} from '@doveit/bricks';
import { uniqueId } from 'lodash';
import useContentsByPropertyId from '../../hooks/use-contents-by-property-id/useContentsByPropertyId';
import { ContentType } from '../../../domain/types';
import { Content, Property } from '../../../providers/api/dtos';
import SimpleModal from '../../../components/simple-modal/SimpleModal';
import MultimediaContentThumbnail from '../../../content/components/MultimediaContentThumbnail';
import MultimediaContentThumbnailSkeleton from '../../../content/components/MultimediaContentThumbnail.skeleton';
import { useSlotSelector } from '../../../hooks/use-slot-selector/useSlotSelector';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';

export type PropertyPhotosSelectorSlotsConfig = Map<number, Content>;

const PHOTO_GRID_SIZE = { XS: 1 / 2, MD: 1 / 3, LG: 1 / 5 };

export interface PropertyPhotosSelectorProps {
  propertyId: NonNullable<Property['id']>,
  totalSlots: number,
  disabled?: boolean,
  onChange?: (slots: PropertyPhotosSelectorSlotsConfig) => void,
}

type ModalData = {
  slotIndex: number,
  content?: Content,
};

const PropertyPhotosSelector: React.FC<PropertyPhotosSelectorProps> = ({
  propertyId,
  totalSlots,
  disabled = false,
  onChange,
}) => {
  const isMobile = useIsDevice('mobile');

  const {
    data: photos,
    isLoading: isPhotosLoading,
    error: photosError,
  } = useContentsByPropertyId(propertyId, ContentType.FOTO);

  const { slots, assignSlot, deselectSlot } = useSlotSelector<Content>({
    totalSlots,
    initialItems: photos,
    resetKey: propertyId,
    onChange,
  });

  const modal = useModal<ModalData>();

  const selectedPhotoIds = React.useMemo(() => new Set([...slots.values()].map((c) => c.id!)), [slots]);

  const openModal = React.useCallback((slotIndex: number) => () => {
    if (disabled) return;

    modal.open({ slotIndex, content: slots.get(slotIndex) });
  }, [modal, slots, disabled]);

  const handlePhotoClick = React.useCallback((content: Content) => {
    if (!modal.data) return;

    const isAlreadySelected = modal.data.content?.id === content.id;

    modal.setData({
      slotIndex: modal.data.slotIndex,
      content: isAlreadySelected ? undefined : content,
    });
  }, [modal]);

  const confirmSelection = React.useCallback(() => {
    if (typeof modal.data?.slotIndex === 'undefined' || !modal.data.content) return;

    assignSlot(modal.data.slotIndex, modal.data.content);

    modal.close();
  }, [assignSlot, modal]);

  const slotKeys = React.useMemo(
    () => Array.from({ length: totalSlots }, () => uniqueId('photos-slot-')),
    [totalSlots],
  );

  return (
    <>
      <Stack gap={100} wrap="nowrap">
        <Text.Body as="div" fontWeight={FontWeight.MEDIUM}>
          Foto anteprima immobile ({slots.size} di {totalSlots})
        </Text.Body>

        {photosError && (
          <Message
            type="critical"
            message="Non è stato possibile caricare le foto dell'immobile."
          />
        )}

        {isPhotosLoading && (
          <Grid gutter={25} aria-label="Foto in caricamento">
            {Array.from({ length: totalSlots }).map((_, slotIndex) => (
              <Grid.Unit
                key={slotKeys[slotIndex]}
                size={PHOTO_GRID_SIZE}
              >
                <MultimediaContentThumbnailSkeleton aspectRatio="3 / 2" />
              </Grid.Unit>
            ))}
          </Grid>
        )}

        {photos && photos.length === 0 && (
          <Message
            type="warning"
            message="L'immobile non ha nessuna foto caricata."
          />
        )}

        {photos && photos.length > 0 && (
          <Grid gutter={25} aria-label="Lista foto">
            {Array.from({ length: totalSlots }).map((_, slotIndex) => {
              const assignedPhoto = slots.get(slotIndex);

              return (
                <Grid.Unit
                  key={slotKeys[slotIndex]}
                  size={PHOTO_GRID_SIZE}
                >
                  <MultimediaContentThumbnail
                    picture={assignedPhoto?.url ? {
                      src: assignedPhoto.url,
                      alt: `Foto ${slotIndex + 1}`,
                    } : undefined}
                    index={slotIndex + 1}
                    aspectRatio="3 / 2"
                    operation={assignedPhoto ? 'view' : 'add'}
                    disabled={disabled}
                    placeholderIcon={assignedPhoto ? ICON_IMAGE_OUTLINE : ICON_PLUS}
                    onClick={!assignedPhoto ? openModal(slotIndex) : undefined}
                    actions={assignedPhoto ? [
                      <ActionIcon
                        size={isMobile ? 'S' : 'XS'}
                        label="Cambia"
                        aria-label="Azione per cambiare la foto selezionata"
                        icon={{ path: ICON_SYNC }}
                        disabled={disabled}
                        onClick={openModal(slotIndex)}
                      />,
                      <ActionIcon
                        size={isMobile ? 'S' : 'XS'}
                        label="Deseleziona"
                        aria-label="Azione per deselezionare la foto"
                        icon={{ path: ICON_CLOSE }}
                        disabled={disabled}
                        onClick={() => deselectSlot(slotIndex)}
                      />,
                    ] : undefined}
                  />
                </Grid.Unit>
              );
            })}
          </Grid>
        )}
      </Stack>

      <Portal>
        <SimpleModal
          {...modal}
          title={`Seleziona foto per slot #${modal.data !== undefined ? modal.data.slotIndex + 1 : ''}`}
          aria-label={`Seleziona foto per slot #${modal.data !== undefined ? modal.data.slotIndex + 1 : ''}`}
          footer={(
            <Action
              color="primary"
              label="Conferma"
              emphasis="high"
              disabled={!modal.data?.content}
              onClick={confirmSelection}
            />
          )}
        >
          {photos && (
            <Grid gutter={25}>
              {photos.map((photo, i) => {
                const isSelectedInThisModal = modal.data?.content?.id === photo.id;
                const isSelectedInAnotherSlot = selectedPhotoIds.has(photo.id!) && slots.get(modal.data?.slotIndex ?? -1)?.id !== photo.id;
                const isClickable = !isSelectedInAnotherSlot;
                const isSelected = isSelectedInThisModal || isSelectedInAnotherSlot;

                if (!photo.id) return null;

                return (
                  <Grid.Unit
                    key={photo.id}
                    size={PHOTO_GRID_SIZE}
                  >
                    <MultimediaContentThumbnail
                      picture={photo.url ? {
                        src: photo.url,
                        alt: `Foto ${i + 1}`,
                      } : undefined}
                      aspectRatio="3 / 2"
                      active={isSelected}
                      placeholderIcon={isSelected ? ICON_IMAGE_OUTLINE : ICON_PLUS}
                      disabled={isSelectedInAnotherSlot}
                      operation={isSelected ? 'remove' : 'add'}
                      onClick={isClickable ? () => handlePhotoClick(photo) : undefined}
                    />
                  </Grid.Unit>
                );
              })}
            </Grid>
          )}
        </SimpleModal>
      </Portal>
    </>
  );
};

export default PropertyPhotosSelector;
